/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useRef, useEffect } from 'react';
import { css } from 'aphrodite';
import { Track } from 'twilio-video';
import { mixins } from '@blend/consumer-components';
import { useMediaStreamTrack } from '../../../hooks/videoProvider/useMediaStreamTrack';
import { ConsumerAppConfigContext, useConsumerTheme } from '../../../hooks/useConsumerTheme';
import { mediaBreakpoints } from '../../../common/responsive';

const generateStyles = (appConfig: ConsumerAppConfigContext) => ({
  videoWrapper: {
    width: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    backgroundColor: appConfig.colors.darkerGray,
  },
  dominantVideoWrapper: {
    boxSizing: 'border-box',
    ...mixins.borderRadius(2),
    ...mixins.border(2, 'solid', appConfig.colors.lightGreen),

    [mediaBreakpoints.desktop]: {
      ...mixins.border(4, 'solid', appConfig.colors.action),
    },
  },
});

interface VideoTrackProps {
  track: VideoProvider.ILocalVideoTrack;
  isDominantSpeaker?: boolean;
  isLocal?: boolean;
  priority?: Track.Priority | null;
}

export function VideoTrack({ track, isDominantSpeaker, isLocal, priority }: VideoTrackProps) {
  const ref = useRef<HTMLVideoElement>(null!);
  const { isCameraFrontFacing } = useMediaStreamTrack(track);
  const { styles } = useConsumerTheme(generateStyles);
  useEffect(() => {
    async function attachAsync() {
      const el = ref.current;
      el.muted = true;
      if (track.setPriority && priority) {
        track.setPriority(priority);
      }
      await Promise.resolve(track.attach(el));
    }
    async function detachAsync() {
      const el = ref.current;
      await Promise.resolve(track.detach(el));
      if (track.setPriority && priority) {
        // Passing `null` to setPriority will set the track's priority to that which it was published with.
        track.setPriority(null);
      }
    }
    attachAsync();
    return () => {
      detachAsync();
    };
  }, [track, priority]);

  // The local video track is mirrored if it is not facing the environment.
  const style = isLocal && isCameraFrontFacing ? { transform: 'scaleX(-1)' } : {};
  return (
    <video
      className={css(styles.videoWrapper, isDominantSpeaker && styles.dominantVideoWrapper)}
      ref={ref}
      style={style}
      playsInline
    />
  );
}
